// Shared registry + resolver for PROETHICS principle illustrations.
// Reused by ReviewFlipGrid (Module 3) and PerspectiveFlipGrid (Module 7) so a
// principle name from session data resolves to the bundled illustration asset.

export const PRINCIPLE_ILLUSTRATIONS = {
	promotingSolutionDiscovery: require("./principle-01-promoting-solution-discovery-illustration-v2.png"),
	riskMitigation: require("./principle-02-risk-mitigation-illustration-v2.png"),
	objectivityNeutrality: require("./principle-03-objectivity-neutrality-maintained-illustration-v3.png"),
	ensuringStudentWelfare: require("./principle-04-ensuring-student-welfare-illustration-v2.png"),
	transparency: require("./principle-05-transparency-actions-communications-illustration-v2.png"),
	boundaries: require("./principle-06-boundaries-role-time-place-illustration-v2.png"),
	integrityProfession: require("./principle-07-integrity-profession-illustration-v2.png"),
	codeEthicsFidelity: require("./principle-08-code-ethics-fidelity-illustration-v2.png"),
	stewardshipLaw: require("./principle-09-stewardship-law-public-trust-illustration-v2.png"),
} as const;

// Canonical principle name → illustration key. Names are normalized (see
// `normalize`) before lookup so comma/plural/casing variants still match.
const PRINCIPLE_KEY_BY_NAME: {
	name: string;
	key: keyof typeof PRINCIPLE_ILLUSTRATIONS;
}[] = [
	{ name: "Promoting Solution Discovery", key: "promotingSolutionDiscovery" },
	{ name: "Risk Mitigation", key: "riskMitigation" },
	{
		name: "Objectivity and Neutrality Are Maintained",
		key: "objectivityNeutrality",
	},
	{ name: "Ensuring Student Welfare", key: "ensuringStudentWelfare" },
	{ name: "Transparency in Actions and Communications", key: "transparency" },
	{
		name: "Honoring the Boundaries of Role, Time and Place",
		key: "boundaries",
	},
	{
		name: "Integrity of the Profession Is Promoted by the Educator",
		key: "integrityProfession",
	},
	{
		name: "Code of Ethics for Educators Is Maintained with Fidelity",
		key: "codeEthicsFidelity",
	},
	{ name: "Stewardship of the Law and Public Trust", key: "stewardshipLaw" },
];

const normalize = (value: string) =>
	value.toLowerCase().replace(/[^a-z0-9]/g, "");

const NORMALIZED_ENTRIES = PRINCIPLE_KEY_BY_NAME.map((entry) => ({
	normalized: normalize(entry.name),
	source: PRINCIPLE_ILLUSTRATIONS[entry.key],
}));

/**
 * Resolve a principle name (e.g. the first line of a principle block) to its
 * bundled illustration. Tolerant of trailing punctuation, plural, and comma
 * differences. Returns undefined when no match is found.
 */
export const getPrincipleIllustration = (name?: string): number | undefined => {
	if (!name) {
		return undefined;
	}
	const input = normalize(name);
	if (!input) {
		return undefined;
	}
	const match = NORMALIZED_ENTRIES.find(
		(entry) =>
			entry.normalized === input ||
			entry.normalized.startsWith(input) ||
			input.startsWith(entry.normalized),
	);
	return match?.source;
};
